import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RESC = { success: "bg-emerald-100 text-emerald-700", denied: "bg-red-100 text-red-700", error: "bg-amber-100 text-amber-700" };

export default function Audit() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canExport = hasRole("super_admin", "direction");
  const [rows, setRows] = useState([]);
  const [searchParams] = useSearchParams();
  const [f, setF] = useState({ module: searchParams.get("module") || "", action: "", date_from: "", date_to: "" });
  const [facets, setFacets] = useState({ modules: [], actions: [] });
  useEffect(() => { api.get("/audit-logs/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);

  const load = () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/audit-logs", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  const fmt = (d) => {
    if (d.created_at_local) return d.created_at_local;
    const iso = d.created_at_utc || d.created_at;
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
  };

  const exportCsv = async () => {
    if (!window.confirm(t("confirmExport"))) return;
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/audit-logs/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `audit_logs_${new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "-")}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export CSV généré");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Export refusé"); }
  };

  return (
    <div data-testid="school-audit">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("auditRegister")}</h1>
        {canExport && <Button data-testid="audit-export-btn" onClick={exportCsv} className="rounded-full gap-2"><Download className="h-4 w-4" />{t("exportCsv")}</Button>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={f.module || "all"} onValueChange={(v) => setF({ ...f, module: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="audit-filter-module" className="w-44"><SelectValue placeholder={t("module")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allModules")}</SelectItem>{facets.modules.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.action || "all"} onValueChange={(v) => setF({ ...f, action: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="audit-filter-action" className="w-56"><SelectValue placeholder={t("action")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allActions")}</SelectItem>{facets.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="audit-filter-from" type="date" className="w-40" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <Input data-testid="audit-filter-to" type="date" className="w-40" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
      </div>
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("timestamp")}</TableHead><TableHead>{t("user")}</TableHead><TableHead>Rôle</TableHead>
            <TableHead>{t("action")}</TableHead><TableHead>{t("module")}</TableHead><TableHead>Objet</TableHead><TableHead>{t("result")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.slice(0, 300).map((r) => (
              <TableRow key={r.id} data-testid="audit-row">
                <TableCell className="text-xs tabular-nums text-slate-600 whitespace-nowrap">{fmt(r)}</TableCell>
                <TableCell className="text-slate-700">{r.user_email || "—"}</TableCell>
                <TableCell><Badge variant="secondary">{r.active_role || r.role_id || "—"}</Badge></TableCell>
                <TableCell className="font-medium text-slate-800">{r.action}</TableCell>
                <TableCell className="text-slate-500">{r.module || "—"}</TableCell>
                <TableCell className="text-slate-500 text-xs">{r.entity_type || r.entity || "—"}</TableCell>
                <TableCell><Badge className={`${RESC[r.result] || "bg-slate-100 text-slate-600"} border-0`}>{r.result || "success"}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
